import React, { useState, useRef, useEffect } from 'react';
import { 
  Menu, X, Moon, Sun, 
  Home, BookOpen, Quote, Shield, LogOut,
  Feather, Book, Lightbulb, ChevronDown
} from 'lucide-react';

const Navbar = ({ 
  activeTab, 
  isMenuOpen, 
  setIsMenuOpen, 
  isDarkMode, 
  toggleDarkMode, 
  isAdminLoggedIn, 
  handleNavigate, 
  handleLogout, 
  closeEditor,
  theme 
}) => {
  // Design tokens aligned with index.css
  const pillStyle = { borderRadius: '9999px' };
  const cardStyle = { borderRadius: '32px' };

  // Primary links always visible on desktop
  const primaryLinks = [
    { id: 'home', label: 'Home', icon: <Home size={18} /> },
    { id: 'blog', label: 'Blog', icon: <BookOpen size={18} /> },
    { id: 'quotes', label: 'Quotes', icon: <Quote size={18} /> },
  ];

  // Secondary links grouped under "More" dropdown
  const moreLinks = [
    { id: 'poems', label: 'Poems', icon: <Feather size={18} /> },
    { id: 'diary', label: 'Reading Diary', icon: <Book size={18} /> },
    { id: 'bank-idea', label: 'Bank Idea', icon: <Lightbulb size={18} /> },
    { id: 'admin', label: 'Admin', icon: <Shield size={18} /> },
  ];

  // All links for mobile menu
  const allLinks = [
    ...primaryLinks,
    ...moreLinks,
  ];

  // "More" dropdown state
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreRef = useRef(null);

  // Close "More" dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setIsMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMoreClick = (link) => {
    setIsMoreOpen(false);
    if (link.external) {
      window.open(link.external, '_blank', 'noopener,noreferrer');
    } else {
      handleNavigate(link.id);
    }
  };

  return (
    <nav className={`fixed top-0 inset-x-0 z-50 px-6 py-4 transition-colors duration-500 border-b backdrop-blur-md ${
      isDarkMode ? 'bg-stone-950/80 border-stone-800' : 'bg-white/80 border-stone-200/50'
    }`}>
      <div className="max-w-5xl mx-auto flex items-center justify-between">
        {/* Logo */}
        <button 
          onClick={() => { handleNavigate('home'); setIsMenuOpen(false); }}
          className={`text-2xl font-serif font-bold tracking-tighter hover:opacity-70 transition-opacity ${theme.text}`}
        >
          idia<span className={`opacity-80 ${isDarkMode ? 'text-stone-300' : 'text-stone-500'}`}>wisdom.com</span>
        </button>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1">
          {primaryLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => handleNavigate(link.id)}
              style={pillStyle}
              className={`px-5 py-2 text-[10px] font-bold uppercase tracking-[0.2em] transition-all ${
                activeTab === link.id 
                  ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-100 text-stone-900') 
                  : `opacity-70 hover:opacity-100 ${theme.text}`
              }`}
            >
              {link.label}
            </button>
          ))}

          {/* "More" Dropdown */}
          <div className="relative" ref={moreRef}>
            <button
              onClick={() => setIsMoreOpen(!isMoreOpen)}
              style={pillStyle}
              className={`flex items-center gap-1 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] transition-all ${
                moreLinks.some(l => activeTab === l.id)
                  ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-100 text-stone-900')
                  : `opacity-70 hover:opacity-100 ${theme.text}`
              }`}
            >
              <span>More</span>
              <ChevronDown size={12} className={`transition-transform ${isMoreOpen ? 'rotate-180' : ''}`} />
            </button>

            {isMoreOpen && (
              <div
                style={cardStyle}
                className={`absolute right-0 top-full mt-2 w-56 p-2 shadow-2xl border z-50 ${
                  isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'
                }`}
              >
                {moreLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => handleMoreClick(link)}
                    style={pillStyle}
                    className={`w-full flex items-center gap-3 px-5 py-3 text-[10px] font-bold uppercase tracking-[0.2em] transition-all ${
                      activeTab === link.id && !link.external
                        ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-100 text-stone-900')
                        : `opacity-70 hover:opacity-100 ${theme.text}`
                    }`}
                  >
                    <span className="opacity-50">{link.icon}</span>
                    <span>{link.label}</span>
                    {link.external && (
                      <span className="ml-auto text-[8px] opacity-30">↗</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className={`w-px h-4 mx-2 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-200'}`} />

          {/* Dark Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            style={pillStyle}
            className={`p-2.5 transition-all ${isDarkMode ? 'text-amber-400 hover:bg-stone-800' : 'text-stone-400 hover:bg-stone-100'}`}
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {isAdminLoggedIn && (
            <button
              onClick={handleLogout}
              style={pillStyle}
              className="p-2.5 text-red-500 hover:bg-red-500/10 transition-all"
            >
              <LogOut size={18} />
            </button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-4 md:hidden">
           <button
            onClick={toggleDarkMode}
            style={pillStyle}
            className={`p-2.5 border ${isDarkMode ? 'border-stone-800 text-amber-400' : 'border-stone-100 text-stone-400'}`}
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={theme.text}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 p-4 animate-in fade-in zoom-in-95 duration-300">
          <div 
            className="absolute inset-0 bg-black/20 backdrop-blur-md" 
            onClick={() => setIsMenuOpen(false)}
          />
          
          <div 
            style={cardStyle}
            className={`relative w-full max-w-sm mx-auto mt-20 p-8 shadow-2xl border ${
              isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'
            }`}
          >
            <div className="flex flex-col gap-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] opacity-60 px-4 mb-2">Navigation</p>
              {allLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => {
                    if (link.external) {
                      window.open(link.external, '_blank', 'noopener,noreferrer');
                    } else {
                      handleNavigate(link.id);
                    }
                    setIsMenuOpen(false);
                  }}
                  style={pillStyle}
                  className={`flex items-center gap-4 px-6 py-4 text-xs font-bold uppercase tracking-widest transition-all ${
                    activeTab === link.id && !link.external
                      ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') 
                      : `opacity-70 hover:opacity-100 ${theme.text}`
                  }`}
                >
                  {link.icon}
                  <span>{link.label}</span>
                  {link.external && (
                    <span className="ml-auto text-[9px] opacity-30">↗</span>
                  )}
                </button>
              ))}

              {isAdminLoggedIn && (
                <button
                  onClick={() => { handleLogout(); setIsMenuOpen(false); }}
                  style={pillStyle}
                  className="flex items-center gap-4 px-6 py-4 text-xs font-bold uppercase tracking-widest text-red-500 hover:bg-red-500/10 transition-all mt-4"
                >
                  <LogOut size={18} />
                  Logout
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;